import type { InternalAxiosRequestConfig } from 'axios';

export const ENCRYPTED_API_HEADER = '20aaencaa20-v1-rm-rf-f';
/** Query-param fallback if the server rejects the hyphenated header name. */
export const ENCRYPTED_API_HEADER_QUERY_FALLBACK = '20aaencaa20_v1_rm_rf_f';

const ENVELOPE_SLOT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] as const;
export type EncryptedApiSlotKey = (typeof ENVELOPE_SLOT_KEYS)[number];

export type EncryptedApiEnvelope = Record<EncryptedApiSlotKey, string> & { value: string };

export type EncryptionRequestConfig = InternalAxiosRequestConfig & {
  _encryptionUseQueryFallback?: boolean;
};

const RSA_KEY_PARAMS: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: 'SHA-256',
};

let sessionEncryptionKeyPair: {
  publicKeyBase64: string;
  privateKey: CryptoKey;
} | null = null;

export function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function toBufferSource(bytes: Uint8Array): BufferSource {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

export function isDecompressionStreamSupported(): boolean {
  return typeof DecompressionStream !== 'undefined';
}

export async function gunzipBytes(data: Uint8Array): Promise<Uint8Array> {
  if (!isDecompressionStreamSupported()) {
    throw new Error('Gzip decompression is not supported in this environment');
  }
  const ds = new DecompressionStream('gzip');
  const stream = new Blob([toBufferSource(data)]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function generateEncryptionKeyPair(): Promise<{
  publicKeyBase64: string;
  privateKey: CryptoKey;
}> {
  const keyPair = await crypto.subtle.generateKey(RSA_KEY_PARAMS, false, ['encrypt', 'decrypt']);
  const spki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  return {
    publicKeyBase64: arrayBufferToBase64(spki),
    privateKey: keyPair.privateKey,
  };
}

/** One RSA keypair per browser session — reused for all encrypted GETs. */
export async function getSessionEncryptionKeyPair(): Promise<{
  publicKeyBase64: string;
  privateKey: CryptoKey;
}> {
  if (!sessionEncryptionKeyPair) {
    sessionEncryptionKeyPair = await generateEncryptionKeyPair();
  }
  return sessionEncryptionKeyPair;
}

export function clearSessionEncryptionKeyPair(): void {
  sessionEncryptionKeyPair = null;
}

function pathFromUrl(url: string): string {
  try {
    // Handles both absolute and relative URLs.
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    return new URL(url, origin).pathname;
  } catch {
    return url.split('?')[0];
  }
}

/** Resolve a possibly-relative axios URL against baseURL. */
export function resolveRequestUrl(url: string | undefined, baseURL?: string): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (baseURL) {
    try {
      return new URL(trimmed, baseURL.endsWith('/') ? baseURL : `${baseURL}/`).href;
    } catch {
      // fall through
    }
  }
  return trimmed;
}

/**
 * Encrypted GET requests only. Writes (POST/PATCH/DELETE) stay plaintext.
 * @see protocol docs for the exact route list.
 */
export function requiresEncryptedApiRequest(
  url: string | undefined,
  method: string | undefined = 'get',
  baseURL?: string,
): boolean {
  if (!url) return false;
  if ((method ?? 'get').toLowerCase() !== 'get') return false;

  const path = pathFromUrl(resolveRequestUrl(url, baseURL));

  // Explicit plaintext GET routes
  if (path === '/bidhi') return false;

  if (path === '/grantha') return true;
  if (path === '/grantha/accessible') return true;
  if (/^\/grantha\/\d+$/.test(path)) return true;
  if (/^\/grantha\/\d+\/shloka\/search$/.test(path)) return true;
  if (path === '/bidhi/roots') return true;
  if (path === '/bidhi/roots/version') return true;
  if (path === '/bidhi/root') return true;
  if (/^\/bidhi\/\d+$/.test(path)) return true;

  // All grantha-shloka GETs are encrypted; POST/PATCH/DELETE stay plaintext (method check above).
  if (path === '/grantha-shloka' || path.startsWith('/grantha-shloka/')) return true;

  // vyakhyanuvad area — all GETs encrypted; writes stay plaintext.
  if (path === '/vyakhyanuvad' || path.startsWith('/vyakhyanuvad/')) return true;
  if (path === '/vyakhyanuvad-translation' || path.startsWith('/vyakhyanuvad-translation/')) return true;
  if (path === '/vyakhyanuvad-text' || path.startsWith('/vyakhyanuvad-text/')) return true;

  return false;
}

/** @deprecated Use requiresEncryptedApiRequest */
export const requiresEncryptedApiResponse = requiresEncryptedApiRequest;

export function isEncryptedApiEnvelope(data: unknown): data is EncryptedApiEnvelope {
  if (!data || typeof data !== 'object') return false;
  const envelope = data as Record<string, unknown>;
  if (typeof envelope.value !== 'string') return false;
  for (const key of ENVELOPE_SLOT_KEYS) {
    if (typeof envelope[key] !== 'string') return false;
  }
  return true;
}

/** (SHA-256(decoded value bytes)[0] % 10) + 1 → slot 1…10 */
export async function computeWrapSlot(packed: Uint8Array): Promise<number> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', toBufferSource(packed)));
  return (digest[0] % 10) + 1;
}

export async function decryptEncryptedApiEnvelope(
  envelope: EncryptedApiEnvelope,
  privateKey: CryptoKey,
): Promise<unknown> {
  const packed = base64ToUint8Array(envelope.value);
  if (packed.length < 12 + 16) {
    throw new Error('Invalid encrypted value: packed payload too short');
  }

  const slot = await computeWrapSlot(packed);
  const wrappedKey = base64ToUint8Array(envelope[String(slot) as EncryptedApiSlotKey]);

  const rawAesKey = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    toBufferSource(wrappedKey),
  );
  if (rawAesKey.byteLength !== 32) {
    throw new Error('Unexpected AES key length after RSA unwrap');
  }

  const aesKey = await crypto.subtle.importKey(
    'raw',
    rawAesKey,
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );

  const iv = packed.slice(0, 12);
  const tag = packed.slice(packed.length - 16);
  const ciphertext = packed.slice(12, packed.length - 16);
  const combined = new Uint8Array(ciphertext.length + tag.length);
  combined.set(ciphertext, 0);
  combined.set(tag, ciphertext.length);

  const gzipBytes = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBufferSource(iv), tagLength: 128 },
    aesKey,
    toBufferSource(combined),
  );

  const jsonBytes = await gunzipBytes(new Uint8Array(gzipBytes));
  return JSON.parse(new TextDecoder().decode(jsonBytes));
}

/** Normalize list payloads that may be a bare array or wrapped in common keys. */
export function unwrapApiList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (isEncryptedApiEnvelope(data)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[encrypted-api] unwrapApiList: encrypted envelope was not decrypted', data);
    }
    return [];
  }
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const listKeys = ['data', 'items', 'granthas', 'results', 'records', 'accessible'];
    for (const key of listKeys) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn('[encrypted-api] unwrapApiList: unrecognized list shape', data);
    }
  }
  return [];
}
